import { Component, effect, OnDestroy, OnInit, Signal } from '@angular/core';

import { RouterModule } from '@angular/router';
import { Product } from '../../models/product';
import { ProductService } from '../../services/product/product.service';
import { ContextService } from '../../services/context/context-signal.service';
import { Context } from '../../models/context';
import { PageHeaderService } from '../../services/page-header/page-header.service';
import {
  UiButtonComponent,
  UiButtonGroupComponent,
  UiListComponent,
  UiListItemComponent
} from '../../shared/ui';

@Component({
  selector: 'app-expiring-products',
  standalone: true,
  imports: [
    RouterModule,
    UiButtonComponent,
    UiButtonGroupComponent,
    UiListComponent,
    UiListItemComponent
],
  templateUrl: './expiring-products.component.html',
  styleUrls: ['./expiring-products.component.scss']
})
export class ExpiringProductsComponent implements OnInit, OnDestroy {

  context: Signal<Context> = this.contextService.context;

  products: Product[] = [];
  loading = false;
  selectedFilter: 'expired' | '7' | '14' = '7';

  constructor(
    private productService: ProductService,
    private contextService: ContextService,
    private pageHeaderService: PageHeaderService
  ) {
    // react to context changes
    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      const hid = c.household?.id;
      if (hid != null) {
        this.loadForFilter(this.selectedFilter, hid);
      }
    });
  }

  ngOnInit(): void {
    this.pageHeaderService.setPageTitle('Expiring Products');
    this.pageHeaderService.setBackButton({
      label: 'Back',
      link: '/home',
      display: 'auto'
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
  }

  setFilter(filter: 'expired' | '7' | '14') {
    if (this.selectedFilter === filter) {
      return;
    }

    this.selectedFilter = filter;
    const c = this.context();
    if (!c?.isSet?.()) return;
    const hid = c.household?.id;
    if (hid != null) {
      this.loadForFilter(filter, hid);
    }
  }

  private loadForFilter(filter: 'expired' | '7' | '14', householdId: number) {
    this.loading = true;
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);

    let from: string | undefined;
    let to: string | undefined;

    if (filter === 'expired') {
      // up to yesterday (expired before today)
      const d = new Date(); d.setDate(d.getDate() - 1);
      to = d.toISOString().slice(0, 10);
    } else if (filter === '7') {
      from = todayStr;
      const d = new Date(); d.setDate(d.getDate() + 7);
      to = d.toISOString().slice(0, 10);
    } else if (filter === '14') {
      from = todayStr;
      const d = new Date(); d.setDate(d.getDate() + 14);
      to = d.toISOString().slice(0, 10);
    }

    this.productService.getExpiringProducts(householdId, from, to).subscribe({
      next: (prods) => {
        // sort by next_expiration ascending
        this.products = (prods ?? []).slice().sort((a, b) => {
          const ad = a.stock?.next_expiration ?? '';
          const bd = b.stock?.next_expiration ?? '';
          return ad.localeCompare(bd);
        });
      },
      error: () => {
        this.products = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  trackById(i: number, p: Product) {
    return p.id ?? i;
  }

  formatDate(dateStr?: string) {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (e) {
      return dateStr;
    }
  }

}
